import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs.tsx';
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.tsx';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion.tsx';
import { Badge } from '../components/ui/badge.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog.tsx';
import { Textarea } from '../components/ui/textarea.tsx';
import {
  CalendarDays,
  Users,
  TrendingDown,
  TrendingUp,
  FileText,
  Settings,
  History,
  Plus,
  UserCheck,
  Clock,
} from 'lucide-react';
import {
  Line,
  LineChart,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../components/ui/chart.tsx';

const attendanceRecords = [
  {
    id: 1,
    type: 'student',
    name: 'John Doe',
    class: 'JSS 1',
    date: '2024-01-15',
    status: 'present',
    timeIn: '07:45',
    timeOut: '14:30',
  },
  {
    id: 2,
    type: 'staff',
    name: 'Mrs. Sarah Johnson',
    department: 'Mathematics',
    date: '2024-01-15',
    status: 'present',
    timeIn: '07:30',
    timeOut: '15:00',
  },
  {
    id: 3,
    type: 'student',
    name: 'Jane Smith',
    class: 'JSS 2',
    date: '2024-01-15',
    status: 'absent',
    timeIn: null,
    timeOut: null,
  },
  {
    id: 4,
    type: 'student',
    name: 'Mike Johnson',
    class: 'SS 1',
    date: '2024-01-15',
    status: 'late',
    timeIn: '08:15',
    timeOut: '14:30',
  },
  {
    id: 5,
    type: 'staff',
    name: 'Mr. David Wilson',
    department: 'English',
    date: '2024-01-15',
    status: 'absent',
    timeIn: null,
    timeOut: null,
  },
];

const weeklyAttendanceData = [
  { day: 'Mon', studentRate: 95, staffRate: 98 },
  { day: 'Tue', studentRate: 92, staffRate: 96 },
  { day: 'Wed', studentRate: 88, staffRate: 94 },
  { day: 'Thu', studentRate: 90, staffRate: 97 },
  { day: 'Fri', studentRate: 85, staffRate: 95 },
  { day: 'Sat', studentRate: 78, staffRate: 88 },
];

const classAttendanceData = [
  { class: 'JSS 1', present: 28, absent: 2, late: 3 },
  { class: 'JSS 2', present: 25, absent: 4, late: 1 },
  { class: 'JSS 3', present: 30, absent: 1, late: 2 },
  { class: 'SS 1', present: 22, absent: 5, late: 3 },
  { class: 'SS 2', present: 24, absent: 3, late: 2 },
  { class: 'SS 3', present: 20, absent: 6, late: 1 },
];

const attendanceStatusData = [
  { name: 'Present', value: 82, color: '#10b981' },
  { name: 'Absent', value: 12, color: '#ef4444' },
  { name: 'Late', value: 6, color: '#f59e0b' },
];

const monthlyTrendData = [
  { month: 'Sep', studentAvg: 88, staffAvg: 95 },
  { month: 'Oct', studentAvg: 90, staffAvg: 96 },
  { month: 'Nov', studentAvg: 85, staffAvg: 94 },
  { month: 'Dec', studentAvg: 82, staffAvg: 92 },
  { month: 'Jan', studentAvg: 89, staffAvg: 97 },
  { month: 'Feb', studentAvg: 91, staffAvg: 98 },
];

const attendanceSettings = [
  {
    id: 1,
    class: 'JSS 1',
    startTime: '07:45',
    lateThreshold: '08:00',
    endTime: '14:30',
  },
  {
    id: 2,
    class: 'JSS 2',
    startTime: '07:45',
    lateThreshold: '08:00',
    endTime: '14:30',
  },
  {
    id: 3,
    class: 'SS 1',
    startTime: '07:30',
    lateThreshold: '07:45',
    endTime: '15:00',
  },
];

const historicalAttendance = [
  {
    session: '2022/2023',
    studentAverage: 87.5,
    staffAverage: 94.2,
    totalDays: 180,
    records: [
      {
        id: 101,
        type: 'student',
        name: 'Former Student 1',
        class: 'JSS 1',
        date: '2023-01-10',
        status: 'present',
        timeIn: '07:45',
        timeOut: '14:30',
      },
    ],
  },
  {
    session: '2021/2022',
    studentAverage: 85.3,
    staffAverage: 92.8,
    totalDays: 175,
    records: [
      {
        id: 201,
        type: 'staff',
        name: 'Former Teacher 1',
        department: 'Science',
        date: '2022-01-12',
        status: 'present',
        timeIn: '07:30',
        timeOut: '15:00',
      },
    ],
  },
];

export default function AttendancePage() {
  const [attendanceForm, setAttendanceForm] = useState({
    type: 'student',
    name: '',
    class: '',
    department: '',
    status: 'present',
    timeIn: '',
    timeOut: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [settingsForm, setSettingsForm] = useState({
    class: '',
    startTime: '',
    lateThreshold: '',
    endTime: '',
  });

  const [isAttendanceDialogOpen, setIsAttendanceDialogOpen] = useState(false);
  const [attendanceData, setAttendanceData] = useState(attendanceRecords);
  const [settingsData, setSettingsData] = useState(attendanceSettings);

  const currentSessionSummary = {
    totalStudents: 180,
    totalStaff: 25,
    todayPresent: 162,
    todayAbsent: 18,
    attendanceRate: 90,
  };

  const handleAttendanceSubmit = (e) => {
    e.preventDefault();
    const newRecord = {
      id: attendanceData.length + 1,
      ...attendanceForm,
    };
    setAttendanceData([newRecord, ...attendanceData]);
    setAttendanceForm({
      type: 'student',
      name: '',
      class: '',
      department: '',
      status: 'present',
      timeIn: '',
      timeOut: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setIsAttendanceDialogOpen(false);
  };

  const handleSettingsSubmit = (e) => {
    e.preventDefault();
    const newSetting = {
      id: settingsData.length + 1,
      ...settingsForm,
    };
    setSettingsData([...settingsData, newSetting]);
    setSettingsForm({
      class: '',
      startTime: '',
      lateThreshold: '',
      endTime: '',
    });
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    return time;
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="border-purple-200 dark:border-purple-800">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Enrolled
              </CardTitle>
              <Users className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {currentSessionSummary.totalStudents +
                  currentSessionSummary.totalStaff}
              </div>
              <p className="text-xs text-muted-foreground">
                {currentSessionSummary.totalStudents} students,{' '}
                {currentSessionSummary.totalStaff} staff
              </p>
            </CardContent>
          </Card>

          <Card className="border-green-200 dark:border-green-800">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Present Today
              </CardTitle>
              <UserCheck className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {currentSessionSummary.todayPresent}
              </div>
              <p className="text-xs text-muted-foreground">
                {currentSessionSummary.attendanceRate}% attendance rate
              </p>
            </CardContent>
          </Card>

          <Card className="border-red-200 dark:border-red-800">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Absent Today
              </CardTitle>
              <TrendingDown className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {currentSessionSummary.todayAbsent}
              </div>
              <p className="text-xs text-muted-foreground">
                Requires follow-up
              </p>
            </CardContent>
          </Card>

          <Card className="border-orange-200 dark:border-orange-800">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Late Arrivals
              </CardTitle>
              <Clock className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">12</div>
              <p className="text-xs text-muted-foreground">This week average</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Weekly Attendance Rates</CardTitle>
              <CardDescription>
                Student and staff attendance over the week
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  studentRate: {
                    label: 'Student Rate',
                    color: '#8b5cf6',
                  },
                  staffRate: {
                    label: 'Staff Rate',
                    color: '#06b6d4',
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={weeklyAttendanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="studentRate"
                      stackId="1"
                      stroke="var(--color-studentRate)"
                      fill="var(--color-studentRate)"
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="staffRate"
                      stackId="2"
                      stroke="var(--color-staffRate)"
                      fill="var(--color-staffRate)"
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Class Attendance Breakdown</CardTitle>
              <CardDescription>
                Present, absent, and late students by class
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  present: {
                    label: 'Present',
                    color: '#10b981',
                  },
                  absent: {
                    label: 'Absent',
                    color: '#ef4444',
                  },
                  late: {
                    label: 'Late',
                    color: '#f59e0b',
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={classAttendanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="class" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar dataKey="present" fill="var(--color-present)" />
                    <Bar dataKey="absent" fill="var(--color-absent)" />
                    <Bar dataKey="late" fill="var(--color-late)" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Attendance Status Distribution</CardTitle>
              <CardDescription>
                Overall attendance status breakdown
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  value: {
                    label: 'Percentage',
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={attendanceStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {attendanceStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Monthly Attendance Trends</CardTitle>
              <CardDescription>
                Average attendance rates over the last 6 months
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  studentAvg: {
                    label: 'Student Average',
                    color: '#8b5cf6',
                  },
                  staffAvg: {
                    label: 'Staff Average',
                    color: '#06b6d4',
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="studentAvg"
                      stroke="var(--color-studentAvg)"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="staffAvg"
                      stroke="var(--color-staffAvg)"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="records" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger
              value="records"
              className="flex items-center space-x-2"
            >
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Records</span>
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="flex items-center space-x-2"
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
            <TabsTrigger
              value="reports"
              className="flex items-center space-x-2"
            >
              <CalendarDays className="h-4 w-4" />
              <span className="hidden sm:inline">Reports</span>
            </TabsTrigger>
            <TabsTrigger
              value="historical"
              className="flex items-center space-x-2"
            >
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">Historical</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="records" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Attendance Records</CardTitle>
                    <CardDescription>
                      Track daily attendance for students and staff
                    </CardDescription>
                  </div>
                  <Dialog
                    open={isAttendanceDialogOpen}
                    onOpenChange={setIsAttendanceDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                        <Plus className="h-4 w-4 mr-2" />
                        Mark Attendance
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Mark Attendance</DialogTitle>
                        <DialogDescription>
                          Record attendance for students or staff members.
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        onSubmit={handleAttendanceSubmit}
                        className="space-y-4"
                      >
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="attendance-type">Type</Label>
                            <Select
                              value={attendanceForm.type}
                              onValueChange={(value) =>
                                setAttendanceForm({
                                  ...attendanceForm,
                                  type: value,
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="student">Student</SelectItem>
                                <SelectItem value="staff">Staff</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="attendance-status">Status</Label>
                            <Select
                              value={attendanceForm.status}
                              onValueChange={(value) =>
                                setAttendanceForm({
                                  ...attendanceForm,
                                  status: value,
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="present">Present</SelectItem>
                                <SelectItem value="absent">Absent</SelectItem>
                                <SelectItem value="late">Late</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="person-name">Name</Label>
                          <Input
                            id="person-name"
                            placeholder="Enter name"
                            value={attendanceForm.name}
                            onChange={(e) =>
                              setAttendanceForm({
                                ...attendanceForm,
                                name: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        {attendanceForm.type === 'student' ? (
                          <div className="space-y-2">
                            <Label htmlFor="student-class">Class</Label>
                            <Select
                              value={attendanceForm.class}
                              onValueChange={(value) =>
                                setAttendanceForm({
                                  ...attendanceForm,
                                  class: value,
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select class" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="JSS 1">JSS 1</SelectItem>
                                <SelectItem value="JSS 2">JSS 2</SelectItem>
                                <SelectItem value="JSS 3">JSS 3</SelectItem>
                                <SelectItem value="SS 1">SS 1</SelectItem>
                                <SelectItem value="SS 2">SS 2</SelectItem>
                                <SelectItem value="SS 3">SS 3</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Label htmlFor="staff-department">Department</Label>
                            <Input
                              id="staff-department"
                              placeholder="Enter department"
                              value={attendanceForm.department}
                              onChange={(e) =>
                                setAttendanceForm({
                                  ...attendanceForm,
                                  department: e.target.value,
                                })
                              }
                            />
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="time-in">Time In</Label>
                            <Input
                              id="time-in"
                              type="time"
                              value={attendanceForm.timeIn}
                              onChange={(e) =>
                                setAttendanceForm({
                                  ...attendanceForm,
                                  timeIn: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="time-out">Time Out</Label>
                            <Input
                              id="time-out"
                              type="time"
                              value={attendanceForm.timeOut}
                              onChange={(e) =>
                                setAttendanceForm({
                                  ...attendanceForm,
                                  timeOut: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="attendance-date">Date</Label>
                          <Input
                            id="attendance-date"
                            type="date"
                            value={attendanceForm.date}
                            onChange={(e) =>
                              setAttendanceForm({
                                ...attendanceForm,
                                date: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="attendance-notes">
                            Notes (Optional)
                          </Label>
                          <Textarea
                            id="attendance-notes"
                            placeholder="Additional notes"
                            value={attendanceForm.notes}
                            onChange={(e) =>
                              setAttendanceForm({
                                ...attendanceForm,
                                notes: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsAttendanceDialogOpen(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            Mark Attendance
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Class/Dept</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Time In</TableHead>
                        <TableHead>Time Out</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attendanceData.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell>{record.date}</TableCell>
                          <TableCell className="font-medium">
                            {record.name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {record.type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {record.class || record.department}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                record.status === 'present'
                                  ? 'default'
                                  : 'secondary'
                              }
                              className={
                                record.status === 'present'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : record.status === 'absent'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              }
                            >
                              {record.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatTime(record.timeIn)}</TableCell>
                          <TableCell>{formatTime(record.timeOut)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Attendance Settings</CardTitle>
                <CardDescription>
                  Configure attendance times and thresholds for different
                  classes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSettingsSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="settings-class">Class</Label>
                      <Select
                        value={settingsForm.class}
                        onValueChange={(value) =>
                          setSettingsForm({ ...settingsForm, class: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="JSS 1">JSS 1</SelectItem>
                          <SelectItem value="JSS 2">JSS 2</SelectItem>
                          <SelectItem value="JSS 3">JSS 3</SelectItem>
                          <SelectItem value="SS 1">SS 1</SelectItem>
                          <SelectItem value="SS 2">SS 2</SelectItem>
                          <SelectItem value="SS 3">SS 3</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="start-time">Start Time</Label>
                      <Input
                        id="start-time"
                        type="time"
                        value={settingsForm.startTime}
                        onChange={(e) =>
                          setSettingsForm({
                            ...settingsForm,
                            startTime: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="late-threshold">Late Threshold</Label>
                      <Input
                        id="late-threshold"
                        type="time"
                        value={settingsForm.lateThreshold}
                        onChange={(e) =>
                          setSettingsForm({
                            ...settingsForm,
                            lateThreshold: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="end-time">End Time</Label>
                      <Input
                        id="end-time"
                        type="time"
                        value={settingsForm.endTime}
                        onChange={(e) =>
                          setSettingsForm({
                            ...settingsForm,
                            endTime: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Save Settings
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Current Settings</CardTitle>
                <CardDescription>
                  Manage attendance time configurations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {settingsData.map((setting) => (
                    <div
                      key={setting.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="space-y-1">
                        <div className="font-medium">{setting.class}</div>
                        <div className="text-sm text-muted-foreground">
                          Start: {setting.startTime} | Late after:{' '}
                          {setting.lateThreshold} | End: {setting.endTime}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm">
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 bg-transparent"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Attendance Reports</CardTitle>
                <CardDescription>
                  Generate detailed attendance reports and analytics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Card className="p-4">
                    <div className="text-center space-y-2">
                      <FileText className="h-8 w-8 text-purple-600 mx-auto" />
                      <h3 className="font-medium">Daily Report</h3>
                      <p className="text-sm text-muted-foreground">
                        Today's attendance summary
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full bg-transparent"
                      >
                        Generate Report
                      </Button>
                    </div>
                  </Card>

                  <Card className="p-4">
                    <div className="text-center space-y-2">
                      <CalendarDays className="h-8 w-8 text-purple-600 mx-auto" />
                      <h3 className="font-medium">Weekly Report</h3>
                      <p className="text-sm text-muted-foreground">
                        This week's attendance trends
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full bg-transparent"
                      >
                        Generate Report
                      </Button>
                    </div>
                  </Card>

                  <Card className="p-4">
                    <div className="text-center space-y-2">
                      <TrendingUp className="h-8 w-8 text-purple-600 mx-auto" />
                      <h3 className="font-medium">Monthly Report</h3>
                      <p className="text-sm text-muted-foreground">
                        Monthly attendance analysis
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full bg-transparent"
                      >
                        Generate Report
                      </Button>
                    </div>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="historical" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Historical Attendance</CardTitle>
                <CardDescription>
                  View past session attendance data and records
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {historicalAttendance.map((session, index) => (
                    <AccordionItem key={index} value={`session-${index}`}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center justify-between w-full pr-4">
                          <span className="font-medium">{session.session}</span>
                          <div className="flex space-x-4 text-sm">
                            <span className="text-green-600">
                              Students: {session.studentAverage}%
                            </span>
                            <span className="text-blue-600">
                              Staff: {session.staffAverage}%
                            </span>
                            <span className="text-muted-foreground">
                              {session.totalDays} days
                            </span>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-muted rounded-lg">
                              <div className="text-2xl font-bold text-purple-600">
                                {session.studentAverage}%
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Student Average
                              </div>
                            </div>
                            <div className="text-center p-4 bg-muted rounded-lg">
                              <div className="text-2xl font-bold text-green-600">
                                {session.staffAverage}%
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Staff Average
                              </div>
                            </div>
                            <div className="text-center p-4 bg-muted rounded-lg">
                              <div className="text-2xl font-bold text-blue-600">
                                {session.totalDays}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Total School Days
                              </div>
                            </div>
                          </div>

                          <div className="rounded-md border">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Date</TableHead>
                                  <TableHead>Name</TableHead>
                                  <TableHead>Type</TableHead>
                                  <TableHead>Class/Dept</TableHead>
                                  <TableHead>Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {session.records.map((record) => (
                                  <TableRow key={record.id}>
                                    <TableCell>{record.date}</TableCell>
                                    <TableCell className="font-medium">
                                      {record.name}
                                    </TableCell>
                                    <TableCell>
                                      <Badge
                                        variant="outline"
                                        className="capitalize"
                                      >
                                        {record.type}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      {record.class || record.department}
                                    </TableCell>
                                    <TableCell>
                                      <Badge
                                        variant={
                                          record.status === 'present'
                                            ? 'default'
                                            : 'secondary'
                                        }
                                        className={
                                          record.status === 'present'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                        }
                                      >
                                        {record.status}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
